import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import "../styles/reset-password.css";
import globals from '../globals.js';

const ResetPassword = () => {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [infoMessage, setInfoMessage] = useState("");

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const navigate = useNavigate();
    const location = useLocation();

    // Recebe o email da página anterior
    const locationState = location.state as { email?: string; message?: string } | null;
    const email = locationState?.email || "";

    useEffect(() => {
        // Se não tem email, redireciona para a página de recuperação
        if (!email) {
            navigate('/recuperar-senha');
            return;
        }

        // Mostra a mensagem de sucesso se veio da página anterior
        if (locationState?.message) {
            setInfoMessage(locationState.message);
        }
    }, [email, navigate, locationState]);

    // Foca no primeiro input ao carregar
    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleCodeChange = (index: number, value: string) => {
        // Permite apenas números
        if (value && !/^\d$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Move para o próximo input automaticamente
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        // Volta para o input anterior ao pressionar Backspace em campo vazio
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newCode = [...code];

        for (let i = 0; i < pastedData.length; i++) {
            newCode[i] = pastedData[i];
        }

        setCode(newCode);

        // Foca no último input preenchido ou no próximo vazio
        const lastFilledIndex = Math.min(pastedData.length - 1, 5);
        inputRefs.current[lastFilledIndex]?.focus();
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError("");

        const fullCode = code.join('');

        // Validações
        if (fullCode.length !== 6) {
            setError("Digite o código de 6 dígitos.");
            return;
        }

        if (!newPassword) {
            setError("Digite a nova senha.");
            return;
        }

        if (newPassword.length < 6) {
            setError("A senha deve ter pelo menos 6 caracteres.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("As senhas não conferem.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/redefinir-senha`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email,
                    codigo: fullCode,
                    nova_senha: newPassword
                })
            });

            const responseData = await response.json();

            if (responseData.status === 'success') {
                setSuccessMessage(responseData.msg || 'Senha redefinida com sucesso!');
                setShowSuccessModal(true);
            } else {
                throw new Error(responseData.msg || 'Não foi possível redefinir sua senha.');
            }
        } catch (error: any) {
            console.error('Erro:', error);
            setError(error.message || 'Ocorreu um erro. Tente novamente.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        setError("");
        setIsLoading(true);

        try {
            const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/recuperar-senha`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: email })
            });

            const responseData = await response.json();

            if (responseData.status === 'success') {
                setInfoMessage(responseData.msg || 'Novo código enviado!');
                setCode(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
            } else {
                throw new Error(responseData.msg || 'Não foi possível reenviar o código.');
            }
        } catch (error: any) {
            console.error('Erro:', error);
            setError(error.message || 'Ocorreu um erro ao reenviar o código.');
        } finally {
            setIsLoading(false);
        }
    };

    const closeSuccessModal = () => {
        setShowSuccessModal(false);
        navigate('/login');
    };

    return (
        <React.Fragment>
            {/* Loading Modal */}
            {isLoading && (
                <div className="loading-modal-overlay">
                    <div className="loading-modal">
                        <div className="loading-spinner"></div>
                        <p>Processando...</p>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="success-modal-overlay">
                    <div className="success-modal">
                        <div className="success-icon">✓</div>
                        <p>{successMessage}</p>
                        <button onClick={closeSuccessModal}>Ir para Login</button>
                    </div>
                </div>
            )}

            <Header />
            <form className="main-form-container reset-password-form" onSubmit={handleSubmit}>
                {error && <div className="main-error-message">{error}</div>}
                {infoMessage && <div className="main-info-message">{infoMessage}</div>}

                <b className="main-header">Redefinir Senha</b>
                <p className="reset-password-description">
                    Digite o código de 6 dígitos enviado para <strong>{email}</strong> e defina sua nova senha.
                </p>

                <div className="code-input-container">
                    <span>CÓDIGO DE VERIFICAÇÃO</span>
                    <div className="code-inputs">
                        {code.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => { inputRefs.current[index] = el; }}
                                type="text"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleCodeChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={index === 0 ? handlePaste : undefined}
                                className="code-input"
                            />
                        ))}
                    </div>
                    <button type="button" className="resend-code-btn" onClick={handleResendCode}>
                        Reenviar código
                    </button>
                </div>

                <div className="form-group">
                    <span>NOVA SENHA</span>
                    <input
                        type="password"
                        name="newPassword"
                        placeholder="Digite sua nova senha"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                </div>

                <div className="form-group">
                    <span>CONFIRMAR SENHA</span>
                    <input
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirme sua nova senha"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </div>

                <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Redefinindo...' : 'Redefinir Senha'}
                </button>

                <div className="reset-password-links">
                    <span>Lembrou sua senha? <Link to="/login">Fazer login</Link></span>
                </div>
            </form>
        </React.Fragment>
    );
};

export default ResetPassword;
